import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Table,
  Input,
  InputNumber,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Descriptions,
  Badge,
  message,
  Spin,
  Select,
  Popconfirm,
  Divider,
  Alert,
} from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  ShopOutlined,
  RobotOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined,
  DollarOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { userApi, botApi } from '../services/api';
import { formatBeijingTime } from '../utils/dateUtils';

const { Title } = Typography;

const formatUsdt = (value?: number | null) => `${(Number(value) || 0).toFixed(2)} U`;

interface User {
  id: number;
  telegram_id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_admin: boolean;
  is_banned: boolean;
  shop_created: boolean;
  total_earnings: number;
  available_balance: number;
  withdrawable_balance: number;
  auto_withdraw_enabled: boolean;
  referral_code?: string;
  // 2026-10-14 安全加固：列表/详情接口不再返回 recovery_key 明文（账号接管级凭证），
  // 只返回 has_recovery_key 表示是否存在；具体值需点「查看」按需拉取。
  has_recovery_key?: boolean;
  recovery_key?: string;  // 仅在按需拉取后由前端填充
  join_date: string;
  bots_count: number;
}

interface Bot {
  id: number;
  bot_username?: string;
  bot_name?: string;
  bot_id?: number;
  is_active: boolean;
  is_verified: boolean;
  withdrawal_frozen?: boolean;
  shop_name?: string;
  created_at: string;
}

interface UserOrderActivityItem {
  order_id: number;
  order_number: string;
  status: string;
  amount: number;
  product_name?: string;
  bot_id?: number;
  bot_username?: string;
  bot_name?: string;
  created_at?: string;
}

interface UserVisitActivityItem {
  visit_id: number;
  bot_id?: number;
  bot_username?: string;
  bot_name?: string;
  shop_name?: string;
  username?: string;
  visit_date?: string;
  visit_count: number;
  created_at?: string;
}

interface UserActivity {
  purchases: UserOrderActivityItem[];
  cancelled_orders: UserOrderActivityItem[];
  visits: UserVisitActivityItem[];
}

export type UserManagementMode = 'all' | 'merchant';

interface UserManagementPageProps {
  mode?: UserManagementMode;
}

export const UserManagementPage: React.FC<UserManagementPageProps> = ({ mode = 'all' }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  // 排序状态
  const [sortBy, setSortBy] = useState<'join_date' | 'bots_count' | 'total_earnings' | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // 角色筛选
  const [roleFilter, setRoleFilter] = useState<string[]>([]);

  // 调整余额
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [adjustingUser, setAdjustingUser] = useState<User | null>(null);
  const [adjustForm] = Form.useForm();
  const [migrationRepairVisible, setMigrationRepairVisible] = useState(false);
  const [migrationRepairLoading, setMigrationRepairLoading] = useState(false);
  const [migrationRepairResult, setMigrationRepairResult] = useState<any>(null);
  const [migrationRepairForm] = Form.useForm();

  // 查看机器人详情
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userBots, setUserBots] = useState<Bot[]>([]);
  const [botsModalVisible, setBotsModalVisible] = useState(false);
  const [botsLoading, setBotsLoading] = useState(false);
  const [userActivity, setUserActivity] = useState<UserActivity>({
    purchases: [],
    cancelled_orders: [],
    visits: [],
  });
  const [generatingRecoveryKey, setGeneratingRecoveryKey] = useState(false);
  // 恢复密钥改为按需拉取：这里存放「本次点查看后取到的值」，关闭/切换用户即清空
  const [revealedRecoveryKey, setRevealedRecoveryKey] = useState<string | null>(null);
  const [revealingRecoveryKey, setRevealingRecoveryKey] = useState(false);
  const isMerchantMode = mode === 'merchant';
  const pageTitle = isMerchantMode ? '商户管理' : '用户管理';
  const pageTitleIcon = isMerchantMode ? <ShopOutlined /> : <UserOutlined />;
  const entityLabel = isMerchantMode ? '商户' : '用户';
  const searchPlaceholder = isMerchantMode
    ? '搜索商户用户名、姓名、ID、机器人用户名、机器人名称、机器人ID...'
    : '搜索用户名、姓名、ID...';
  const requestRole = isMerchantMode
    ? 'merchant'
    : roleFilter.length === 1
    ? roleFilter[0]
    : undefined;

  // 加载用户列表
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response: any = await userApi.getUsers({
        page: pagination.current,
        pageSize: pagination.pageSize,
        search: searchText || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        role: requestRole,
      });

      setUsers(response || []);
      setPagination({
        ...pagination,
        total: response?.length || 0,
      });
    } catch (error) {
      message.error('加载用户列表失败');
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  // 查看用户的机器人
  const viewUserBots = async (user: User) => {
    try {
      setSelectedUser(user);
      setBotsModalVisible(true);
      setBotsLoading(true);

      const [botsResponse, activityResponse]: any = await Promise.all([
        userApi.getUserBots(user.id),
        userApi.getUserActivity(user.id, { limit: 20 }),
      ]);
      setUserBots(botsResponse || []);
      setUserActivity(activityResponse || { purchases: [], cancelled_orders: [], visits: [] });
    } catch (error) {
      message.error('加载用户详情失败');
      console.error('Failed to fetch user detail:', error);
    } finally {
      setBotsLoading(false);
    }
  };

  // 删除机器人
  const handleDeleteBot = async (botId: number) => {
    try {
      await botApi.deleteBot(botId);
      message.success('机器人已删除');
      // 刷新机器人列表
      if (selectedUser) {
        const response: any = await userApi.getUserBots(selectedUser.id);
        setUserBots(response || []);
        // 更新用户列表中的机器人数量
        fetchUsers();
      }
    } catch (error) {
      const detail = (error as any)?.response?.data?.detail;
      message.error(typeof detail === 'string' && detail.trim() ? detail : '删除机器人失败');
      console.error('Failed to delete bot:', error);
    }
  };

  const confirmDeleteBot = (bot: Bot) => {
    const displayName = bot.bot_username ? `@${bot.bot_username}` : (bot.bot_name || `ID:${bot.id}`);
    Modal.confirm({
      title: '删除机器人',
      content: `确定要删除机器人 ${displayName} 吗？此操作将停止机器人运行。`,
      okText: '确定删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => handleDeleteBot(bot.id),
    });
  };

  // 冻结/解冻机器人提现
  const handleToggleWithdrawalFreeze = async (botId: number, currentFrozen: boolean) => {
    try {
      if (currentFrozen) {
        await botApi.unfreezeWithdrawal(botId);
        message.success('已解冻该机器人的提现功能');
      } else {
        await botApi.freezeWithdrawal(botId);
        message.success('已冻结该机器人的提现功能');
      }
      // 刷新机器人列表
      if (selectedUser) {
        const response: any = await userApi.getUserBots(selectedUser.id);
        setUserBots(response || []);
      }
    } catch (error) {
      message.error(currentFrozen ? '解冻失败' : '冻结失败');
      console.error('Failed to toggle withdrawal freeze:', error);
    }
  };

  // 调整余额
  const handleAdjustBalance = async (values: { amount: number; reason: string }) => {
    if (!adjustingUser) return;
    try {
      await userApi.adjustBalance(adjustingUser.id, values);
      message.success('余额调整成功');
      setAdjustModalVisible(false);
      adjustForm.resetFields();
      setAdjustingUser(null);
      fetchUsers();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail) message.error(detail);
    }
  };

  const handleMigrationRepair = async (values: { source_bot_username: string; target_bot_username: string }) => {
    try {
      setMigrationRepairLoading(true);
      const response: any = await botApi.repairMigration({
        source_bot_username: values.source_bot_username,
        target_bot_username: values.target_bot_username,
      });
      setMigrationRepairResult(response?.summary || null);
      message.success(response?.message || '迁移修复已执行');
      await fetchUsers();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '迁移修复失败');
    } finally {
      setMigrationRepairLoading(false);
    }
  };

  const handleGenerateRecoveryKey = async () => {
    if (!selectedUser) return;

    try {
      setGeneratingRecoveryKey(true);
      const response: any = await userApi.generateRecoveryKey(selectedUser.id);
      const newRecoveryKey = response?.recovery_key;

      if (newRecoveryKey) {
        setSelectedUser((prev) => (
          prev
            ? { ...prev, recovery_key: newRecoveryKey }
            : prev
        ));
      }

      message.success(response?.message || '恢复密钥生成成功');
      await fetchUsers();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '生成恢复密钥失败');
      console.error('Failed to generate recovery key:', error);
    } finally {
      setGeneratingRecoveryKey(false);
    }
  };

  const handleBanToggle = async (user: User, nextBanned: boolean) => {
    try {
      if (nextBanned) {
        await userApi.banUser(user.id);
        message.success('已执行全平台封禁');
      } else {
        await userApi.unbanUser(user.id);
        message.success('已解除全平台封禁');
      }
      await fetchUsers();
    } catch (error) {
      console.error('Failed to toggle user ban status:', error);
    }
  };

  const fallbackCopyRecoveryKey = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      message.success('恢复密钥已复制到剪贴板');
    } catch {
      message.error('复制失败，请手动复制');
    }

    document.body.removeChild(textarea);
  };

  const handleCopyRecoveryKey = async (recoveryKey: string) => {
    if (!recoveryKey) return;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(recoveryKey);
        message.success('恢复密钥已复制到剪贴板');
        return;
      } catch {
        fallbackCopyRecoveryKey(recoveryKey);
        return;
      }
    }

    fallbackCopyRecoveryKey(recoveryKey);
  };

  // 2026-10-14 安全加固：恢复密钥不再随列表返回，点「查看」才单独向后端取一次
  const handleRevealRecoveryKey = async () => {
    if (!selectedUser) return;
    setRevealingRecoveryKey(true);
    try {
      const resp: any = await userApi.getRecoveryKey(selectedUser.id);
      const key = resp?.recovery_key;
      if (key) {
        setRevealedRecoveryKey(key);
      } else {
        message.warning('该用户尚未生成恢复密钥');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '获取恢复密钥失败');
    } finally {
      setRevealingRecoveryKey(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.current, pagination.pageSize, sortBy, sortOrder, requestRole]);

  // 切换用户 / 关闭详情面板时，清掉已展示的恢复密钥，避免串到别的用户身上
  useEffect(() => {
    setRevealedRecoveryKey(null);
  }, [selectedUser?.id]);

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchUsers();
  };

  // 表格列定义
  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Telegram ID',
      dataIndex: 'telegram_id',
      key: 'telegram_id',
      width: 140,
      render: (id: number) => <code>{id}</code>,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
      render: (username: string) => username ? `@${username}` : '-',
    },
    {
      title: '姓名',
      key: 'name',
      width: 150,
      render: (_, record) => {
        const name = [record.first_name, record.last_name].filter(Boolean).join(' ');
        return name || '-';
      },
    },
    {
      title: '角色',
      key: 'role',
      width: 120,
      filters: isMerchantMode
        ? undefined
        : [
            { text: '商户', value: 'merchant' },
            { text: '用户', value: 'user' },
            { text: '管理员', value: 'admin' },
          ],
      filteredValue: isMerchantMode ? null : (roleFilter.length ? roleFilter : null),
      render: (_, record) => (
        <Space>
          {record.shop_created ? (
            <Tag color="orange">商户</Tag>
          ) : (
            <Tag color="default">用户</Tag>
          )}
          {record.is_admin && <Tag color="blue">管理员</Tag>}
          {record.is_banned && <Tag color="error">已封禁</Tag>}
        </Space>
      ),
    },
    {
      title: '绑定机器人',
      dataIndex: 'bots_count',
      key: 'bots_count',
      width: 120,
      render: (count: number, record) => (
        <Button
          type="link"
          icon={<RobotOutlined />}
          onClick={() => viewUserBots(record)}
        >
          {count} 个
        </Button>
      ),
    },
    {
      title: '店铺状态',
      dataIndex: 'shop_created',
      key: 'shop_created',
      width: 100,
      render: (created: boolean) =>
        created ? (
          <Tag color="green">已创建</Tag>
        ) : (
          <Tag color="default">未创建</Tag>
        ),
    },
    {
      title: '恢复密钥',
      dataIndex: 'has_recovery_key',
      key: 'has_recovery_key',
      width: 160,
      // 列表只显示「是否有」，不再暴露密钥明文（安全加固 2026-10-14）。
      // 需要具体值时进详情页点「查看恢复密钥」，按需单独拉取并留审计日志。
      render: (has: boolean) => (
        has ? <Tag color="blue">已生成</Tag> : <Tag color="default">未生成</Tag>
      ),
    },
    {
      title: '总收益',
      dataIndex: 'total_earnings',
      key: 'total_earnings',
      width: 120,
      render: (earnings: number) => formatUsdt(earnings),
    },
    {
      title: '店铺余额',
      dataIndex: 'available_balance',
      key: 'available_balance',
      width: 160,
      render: (balance: number, record) => (
        <Space size={4}>
          <span>{formatUsdt(balance)}</span>
          <Button
            type="link"
            size="small"
            icon={<DollarOutlined />}
            style={{ padding: 0 }}
            onClick={() => {
              setAdjustingUser(record);
              adjustForm.resetFields();
              setAdjustModalVisible(true);
            }}
          >
            调整
          </Button>
        </Space>
      ),
    },
    {
      title: '当前可提现余额',
      dataIndex: 'withdrawable_balance',
      key: 'withdrawable_balance',
      width: 140,
      render: (balance: number) => formatUsdt(balance),
    },
    {
      title: '自动转账',
      dataIndex: 'auto_withdraw_enabled',
      key: 'auto_withdraw_enabled',
      width: 120,
      render: (enabled: boolean) => (enabled ? '已开通' : '未开通'),
    },
    {
      title: '注册时间',
      dataIndex: 'join_date',
      key: 'join_date',
      width: 180,
      render: (date: string) => formatBeijingTime(date),
    },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => {
        if (record.is_admin) {
          return <span style={{ color: '#999' }}>管理员不可封禁</span>;
        }

        return (
          <Popconfirm
            title={record.is_banned ? '解除全平台封禁' : '全平台封禁用户'}
            description={
              record.is_banned
                ? '确认解除该用户的全平台封禁吗？'
                : '确认封禁该用户吗？封禁后将无法进入任意店铺、下单和联系商户。'
            }
            onConfirm={() => handleBanToggle(record, !record.is_banned)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="link"
              danger={!record.is_banned}
              icon={record.is_banned ? <UnlockOutlined /> : <LockOutlined />}
            >
              {record.is_banned ? 'Unban' : 'Ban'}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  // 机器人列表列定义
  const botColumns: ColumnsType<Bot> = [
    {
      title: 'Bot ID',
      dataIndex: 'bot_id',
      key: 'bot_id',
      render: (id: number) => <code>{id || '-'}</code>,
    },
    {
      title: 'Bot 用户名',
      dataIndex: 'bot_username',
      key: 'bot_username',
      render: (username: string) => username ? `@${username}` : '-',
    },
    {
      title: 'Bot 名称',
      dataIndex: 'bot_name',
      key: 'bot_name',
      render: (name: string) => name || '-',
    },
    {
      title: '店铺名称',
      dataIndex: 'shop_name',
      key: 'shop_name',
      render: (name: string) => name || '-',
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record) => (
        <Space>
          {record.is_active ? (
            <Badge status="success" text="运行中" />
          ) : (
            <Badge status="default" text="已停止" />
          )}
          {record.is_verified ? (
            <Tag color="green">已验证</Tag>
          ) : (
            <Tag color="orange">未验证</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatBeijingTime(date),
    },
    {
      title: '提现状态',
      key: 'withdrawal_status',
      width: 100,
      render: (_, record) => (
        record.withdrawal_frozen ? (
          <Tag color="red" icon={<LockOutlined />}>已冻结</Tag>
        ) : (
          <Tag color="green" icon={<UnlockOutlined />}>正常</Tag>
        )
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Popconfirm
            title={record.withdrawal_frozen ? "解冻提现" : "冻结提现"}
            description={record.withdrawal_frozen
              ? `确定要解冻机器人 @${record.bot_username} 的提现功能吗？`
              : `确定要冻结机器人 @${record.bot_username} 的提现功能吗？`
            }
            onConfirm={() => handleToggleWithdrawalFreeze(record.id, record.withdrawal_frozen || false)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              icon={record.withdrawal_frozen ? <UnlockOutlined /> : <LockOutlined />}
              size="small"
            >
              {record.withdrawal_frozen ? '解冻' : '冻结'}
            </Button>
          </Popconfirm>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => confirmDeleteBot(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const activityOrderColumns: ColumnsType<UserOrderActivityItem> = [
    {
      title: '订单号',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (value: string) => <code>{value}</code>,
    },
    {
      title: '商品',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (value?: string) => value || '-',
    },
    {
      title: '机器人',
      key: 'bot',
      render: (_, record) => record.bot_username ? `@${record.bot_username}` : record.bot_name || '-',
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (value: number) => `¥${(value || 0).toFixed(2)}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          paid: { color: 'green', text: '已支付' },
          completed: { color: 'green', text: '已完成' },
          processing: { color: 'blue', text: '处理中' },
          cancelled: { color: 'red', text: '已取消' },
        };
        const item = statusMap[value] || { color: 'default', text: value || '-' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (value?: string) => formatBeijingTime(value),
    },
  ];

  const visitColumns: ColumnsType<UserVisitActivityItem> = [
    {
      title: '机器人',
      key: 'bot',
      render: (_, record) => {
        const main = record.bot_username ? `@${record.bot_username}` : record.bot_name || '-';
        const shop = record.shop_name ? ` / ${record.shop_name}` : '';
        return `${main}${shop}`;
      },
    },
    {
      title: '机器人ID',
      dataIndex: 'bot_id',
      key: 'bot_id',
      width: 110,
      render: (value?: number) => value ? <code>{value}</code> : '-',
    },
    {
      title: '访问日期',
      dataIndex: 'visit_date',
      key: 'visit_date',
      width: 130,
      render: (value?: string) => value ? value.replace(/-/g, '/') : '-',
    },
    {
      title: '次数',
      dataIndex: 'visit_count',
      key: 'visit_count',
      width: 80,
    },
    {
      title: '记录时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (value?: string) => formatBeijingTime(value),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Title level={2}>
          {pageTitleIcon} {pageTitle}
        </Title>
        <Space>
          <Button
            icon={<ToolOutlined />}
            onClick={() => {
              migrationRepairForm.resetFields();
              setMigrationRepairResult(null);
              setMigrationRepairVisible(true);
            }}
          >
            迁移修复
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchUsers}
            loading={loading}
          >
            刷新
          </Button>
        </Space>
      </div>

      <Card>
        <div className="mb-4">
          <Space wrap>
            <Input
              placeholder={searchPlaceholder}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 300 }}
              allowClear
            />
            <Button type="primary" onClick={handleSearch} loading={loading}>
              搜索
            </Button>
            <Select
              placeholder="排序方式"
              style={{ width: 160 }}
              allowClear
              value={sortBy}
              onChange={(value) => {
                setSortBy(value);
                setPagination({ ...pagination, current: 1 });
              }}
              options={[
                { value: 'join_date', label: '按注册时间' },
                { value: 'bots_count', label: '按机器人数量' },
                { value: 'total_earnings', label: '按总收益' },
              ]}
            />
            <Button
              icon={sortOrder === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
              onClick={() => {
                setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                setPagination({ ...pagination, current: 1 });
              }}
            >
              {sortOrder === 'desc' ? '降序' : '升序'}
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          onChange={(_pag, filters) => {
            if (!isMerchantMode) {
              const role = (filters.role as string[]) || [];
              setRoleFilter(role);
            }
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
            },
          }}
          scroll={{ x: 1840 }}
        />
      </Card>

      {/* 用户机器人详情 Modal */}
      <Modal
        title="机器人商品迁移修复"
        open={migrationRepairVisible}
        onCancel={() => {
          setMigrationRepairVisible(false);
          setMigrationRepairResult(null);
          migrationRepairForm.resetFields();
        }}
        onOk={() => migrationRepairForm.submit()}
        okText="执行修复"
        cancelText="取消"
        confirmLoading={migrationRepairLoading}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="用于机器人迁移后商品没有同步的修复"
          description="输入原机器人和新机器人的用户名，系统会把原机器人名下商品迁移到新机器人，并重建商品链接。"
        />
        <Form form={migrationRepairForm} onFinish={handleMigrationRepair} layout="vertical">
          <Form.Item
            name="source_bot_username"
            label="原机器人用户名"
            rules={[{ required: true, message: '请输入原机器人用户名' }]}
          >
            <Input placeholder="@vipziyuan10101bot" allowClear />
          </Form.Item>
          <Form.Item
            name="target_bot_username"
            label="新机器人用户名"
            rules={[{ required: true, message: '请输入新机器人用户名' }]}
          >
            <Input placeholder="@vipziy100001bot" allowClear />
          </Form.Item>
        </Form>
        {migrationRepairResult && (
          <Descriptions
            bordered
            column={2}
            size="small"
            style={{ marginTop: 16 }}
          >
            <Descriptions.Item label="原机器人">
              @{migrationRepairResult.source_bot_username}
            </Descriptions.Item>
            <Descriptions.Item label="新机器人">
              @{migrationRepairResult.target_bot_username}
            </Descriptions.Item>
            <Descriptions.Item label="迁移商品">
              {migrationRepairResult.moved_products || 0}
            </Descriptions.Item>
            <Descriptions.Item label="重建链接">
              {migrationRepairResult.rebuilt_links || 0}
            </Descriptions.Item>
            <Descriptions.Item label="重复商品">
              {migrationRepairResult.duplicate_products || 0}
            </Descriptions.Item>
            <Descriptions.Item label="补群组归属">
              {migrationRepairResult.moved_groups || 0}
            </Descriptions.Item>
            <Descriptions.Item label="原商品剩余">
              {migrationRepairResult.source_product_count_after || 0}
            </Descriptions.Item>
            <Descriptions.Item label="新商品总数">
              {migrationRepairResult.target_product_count_after || 0}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={
          <Space>
            <RobotOutlined />
            <span>
              {selectedUser?.username
                ? `@${selectedUser.username}`
                : selectedUser?.first_name || entityLabel}{' '}
              的详情
            </span>
          </Space>
        }
        open={botsModalVisible}
        onCancel={() => {
          setBotsModalVisible(false);
          setSelectedUser(null);
          setUserBots([]);
          setUserActivity({ purchases: [], cancelled_orders: [], visits: [] });
        }}
        footer={null}
        width={1200}
      >
        <Spin spinning={botsLoading}>
          {selectedUser && (
            <div className="mb-4">
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="用户ID">
                  {selectedUser.id}
                </Descriptions.Item>
                <Descriptions.Item label="Telegram ID">
                  {selectedUser.telegram_id}
                </Descriptions.Item>
                <Descriptions.Item label="推荐码">
                  {selectedUser.referral_code || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="机器人数量">
                  {selectedUser.bots_count}
                </Descriptions.Item>
                <Descriptions.Item label="恢复密钥" span={2}>
                  {selectedUser.has_recovery_key
                    ? (
                      <Space wrap>
                        {revealedRecoveryKey
                          ? (
                            <>
                              <code>{revealedRecoveryKey}</code>
                              <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyRecoveryKey(revealedRecoveryKey)}
                              >
                                复制恢复密钥
                              </Button>
                            </>
                          )
                          : (
                            <Button
                              size="small"
                              onClick={handleRevealRecoveryKey}
                              loading={revealingRecoveryKey}
                            >
                              查看恢复密钥
                            </Button>
                          )}
                      </Space>
                    )
                    : (
                      <Space>
                        <span style={{ color: '#999' }}>未生成</span>
                        <Popconfirm
                          title="生成恢复密钥"
                          description="确定要为该商户生成恢复密钥吗？仅适用于当前尚未生成恢复密钥的历史商户。"
                          onConfirm={handleGenerateRecoveryKey}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button
                            type="primary"
                            size="small"
                            loading={generatingRecoveryKey}
                          >
                            生成恢复密钥
                          </Button>
                        </Popconfirm>
                      </Space>
                    )}
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}

          <Divider orientation="left">平台操作记录</Divider>

          <div className="mb-4">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>最近购买记录</div>
            <Table
              columns={activityOrderColumns}
              dataSource={userActivity.purchases}
              rowKey="order_id"
              pagination={false}
              size="small"
              locale={{ emptyText: '暂无购买记录' }}
              scroll={{ x: 900 }}
            />
          </div>

          <div className="mb-4">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>最近取消记录</div>
            <Table
              columns={activityOrderColumns}
              dataSource={userActivity.cancelled_orders}
              rowKey="order_id"
              pagination={false}
              size="small"
              locale={{ emptyText: '暂无取消记录' }}
              scroll={{ x: 900 }}
            />
          </div>

          <div className="mb-4">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>最近访问机器人记录</div>
            <Table
              columns={visitColumns}
              dataSource={userActivity.visits}
              rowKey="visit_id"
              pagination={false}
              size="small"
              locale={{ emptyText: '暂无访问记录' }}
              scroll={{ x: 800 }}
            />
          </div>

          <Divider orientation="left">绑定机器人</Divider>

          {userBots.length > 0 ? (
            <Table
              columns={botColumns}
              dataSource={userBots}
              rowKey="id"
              pagination={false}
              size="small"
            />
          ) : (
            <div className="text-center py-8 text-gray-400">
              该{entityLabel}还没有绑定任何机器人
            </div>
          )}
        </Spin>
      </Modal>
      {/* 调整余额 Modal */}
      <Modal
        title="调整店铺余额"
        open={adjustModalVisible}
        onCancel={() => {
          setAdjustModalVisible(false);
          setAdjustingUser(null);
          adjustForm.resetFields();
        }}
        onOk={() => adjustForm.submit()}
        okText="确认调整"
        cancelText="取消"
      >
        {adjustingUser && (
          <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="用户">
              {adjustingUser.username ? `@${adjustingUser.username}` : adjustingUser.first_name || `ID:${adjustingUser.id}`}
            </Descriptions.Item>
            <Descriptions.Item label="当前店铺余额">
              {formatUsdt(adjustingUser.available_balance)}
            </Descriptions.Item>
          </Descriptions>
        )}
        <Form form={adjustForm} onFinish={handleAdjustBalance} layout="vertical">
          <Form.Item
            name="amount"
            label="调整金额（U，正数加款，负数扣款）"
            rules={[
              { required: true, message: '请输入调整金额' },
              { type: 'number', transform: Number, message: '请输入有效数字' },
              {
                validator: (_, value) =>
                  value !== undefined && value !== 0
                    ? Promise.resolve()
                    : Promise.reject(new Error('金额不能为0')),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              precision={2}
              placeholder="例如: 50 或 -30"
              addonBefore="U"
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="操作原因"
            rules={[{ required: true, message: '请输入操作原因' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入调整原因，如：客户补差价、订单金额修正等" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const UserManagement: React.FC = () => <UserManagementPage mode="all" />;

export default UserManagement;
